import React, { useEffect, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

import { Download, FileStack, LoaderPinwheel, Table } from "lucide-react";
import {
  dispatchJobBulk,
  forceCompleteBulk,
  getDinBulkReport,
  getGstBulkReport,
  getRcBulkReport,
  updateStatusBulk,
} from "../../services/BulkReportService/BulkService";
import ConfirmModal from "../../components/Billing/ConfirmModal";

function BulkReport() {
  const [isLoading, setIsLoading] = useState(false);
  const [reportType, setReportType] = useState(""); // "din" or "gst"
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState(null);
  const [totalData, setTotalData] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [forceCompleteClicked, setForceCompleteClicked] = useState(false);
  const [retryClicked, setRetryClicked] = useState(false);
  const [dataForActions, setDataForActions] = useState(null);

  // Get appropriate API functions based on report type
  const getReportAPI = () => {
    return reportType === "din"
      ? getDinBulkReport
      : reportType === "rc"
        ? getRcBulkReport
        : getGstBulkReport;
  };

  //   const getDownloadAPI = () => {
  //     return reportType === "din"
  //       ? DinBulkDownloading
  //       : reportType === "rc"
  //         ? downloadBulkRc
  //         : GstBulkDownloading;
  //   };

  const getTitle = () => {
    return reportType === "din" ? " Report" : "Report";
  };

  //   const handleDownload = async (reference_id) => {
  //     if (!reference_id || !profileData) return;
  //     setIsLoading(true);

  //     try {
  //       const response = await getDownloadAPI()({
  //         api_id: profileData?.user_id,
  //         api_key: profileData?.api_key,
  //         token_id: profileData?.token_id,
  //         reference_id,
  //       });

  //       const fileUrl = response?.data?.file;

  //       if (!fileUrl) {
  //         throw new Error("File not available yet");
  //       }

  //       // trigger download
  //       const link = document.createElement("a");
  //       link.href = fileUrl;
  //       link.download = fileUrl.split("/").pop();
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //     } catch (error) {
  //       console.error(error);
  //       showErrorToast(error.message);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  const handleBulkReport = async () => {
    const payload = {};
    if (fromDate) payload.from_date = fromDate;
    if (toDate) payload.to_date = toDate;
    if (totalData) payload.total_data = totalData;
    if (status) payload.status = status;
    if (referenceId) payload.reference_id = referenceId;
    payload.page = page;

    // Add auth params for GST report
    // if (reportType === "gst") {
    //   payload.api_id = profileData.user_id;
    //   payload.api_key = profileData.api_key;
    //   payload.token_id = profileData.token_id;
    // }

    setIsLoading(true);

    try {
      const response = await getReportAPI()(
        Object.keys(payload).length ? payload : undefined,
      );

      if (response?.data) {
        setReportData(response.data);
        setPagination(response.pagination);
      }

      if (response?.status?.code === 200) {
        showSuccessToast(
          response?.data?.message || "Report fetched successfully",
        );
      } else {
        showErrorToast(response?.data?.message || "Failed to fetch report");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to fetch report");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceComplete = async () => {
    setIsLoading(true);
    try {
      const res = await forceCompleteBulk({
        service: reportType,
        id: dataForActions?.id,
      });

      if (res?.success) {
        showSuccessToast(res?.message || res?.data?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setForceCompleteClicked(false);
      setIsLoading(false);
    }
  };

  const handleRetry = async () => {
    setIsLoading(true);
    try {
      const res = await dispatchJobBulk({
          service: reportType,
          id: dataForActions?.id,
        });

      if (res?.success) {
        const newRes = await updateStatusBulk(dataForActions?.id);

        if (newRes?.success) {
          showSuccessToast(newRes?.message || newRes?.data?.message);
        } else {
          showErrorToast(newRes?.message || newRes?.data?.message);
        }
      } else {
        showErrorToast(res?.message || res?.data?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
      setRetryClicked(false)
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [reportType, fromDate, toDate, totalData, status, referenceId]);

  // Fetch report when page or profile changes
  useEffect(() => {
    if (page > 1 || reportData) {
      handleBulkReport();
    }
  }, [page]);

  return (
    <>
      <div className="w-full h-full flex flex-col items-center justify-start gap-2 min-h-screen bg-white px-2 py-2">
        {/* Header */}

        <div className="flex w-full items-center justify-between px-6 py-5 bg-white border-b border-slate-100">
          <div className="flex items-center gap-4">
            {/* Icon Container */}
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 shadow-sm border border-indigo-100/50">
              <FileStack size={22} strokeWidth={2.5} />
            </div>

            <div>
              <h1 className="text-xl font-extrabold tracking-tight text-slate-800 leading-none">
                Bulk Report
              </h1>
              <p className="text-[11px] font-medium text-slate-400 mt-1 uppercase tracking-widest">
                Batch Processing Module
              </p>
            </div>
          </div>

          {/* Action / Status Section */}
          <div className="flex items-center gap-3">
            {/* <button className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
              <RefreshCw size={18} />
            </button> */}
            <div className="h-6 w-[1px] bg-slate-200 mx-1" />
            <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              Live System
            </span>
          </div>
        </div>
        <div
          className={`w-full rounded-xl shadow-sm p-2 ${
            reportType === "din" ? "bg-white" : "bg-yellow-50"
          }`}
        >
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {/* Reference ID */}

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600 block">
                Select Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-800 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" selected>
                  Select Report Type
                </option>
                <option value="din">DIN Report</option>
                <option value="gst">GST To Contact Report</option>
                <option value="rc">Bulk RC Report</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600">
                Reference ID
              </label>
              <input
                type="search"
                value={referenceId || ""}
                placeholder="Search by Reference ID..."
                onChange={(e) => setReferenceId(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* From Date */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600">
                From Date
              </label>
              <input
                type="date"
                value={fromDate || ""}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* To Date */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600">
                To Date
              </label>
              <input
                type="date"
                value={toDate || ""}
                onChange={(e) => setToDate(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600">
                Status
              </label>
              <select
                value={status || ""}
                onChange={(e) => setStatus(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-800 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="completed">COMPLETED</option>
                <option value="pending">PENDING</option>
                <option value="processing">PROCESSING</option>
              </select>
            </div>

            {/* Total Data */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600">
                Total Data
              </label>
              <input
                type="number"
                value={totalData || ""}
                placeholder="Total Records"
                onChange={(e) => setTotalData(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Get Report Button */}
        <button
          onClick={handleBulkReport}
          disabled={isLoading}
          className={`px-6 py-2 text-lg font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
            reportType === "din"
              ? "bg-green-600 text-white hover:bg-green-700"
              : "bg-yellow-400 text-gray-800 hover:bg-yellow-500"
          }`}
        >
          {reportType === "din" ? (
            <Download className="w-5 h-5" />
          ) : (
            <Table className="w-5 h-5" />
          )}
          Get Report
        </button>

        <div className="text-2xl font-bold">{getTitle()}</div>

        {isLoading && (
          <div className="w-full flex justify-center">
            <LoaderPinwheel
              size={40}
              className=" animate-spin text-indigo-600"
            />
          </div>
        )}
        {/* Report Table */}
        {reportData?.length > 0 && (
          <div className="w-full px-3 md:px-2">
            <div className="mt-2 w-full overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
              <table className="min-w-[900px] w-full px-4 text-xs text-left text-gray-700">
                <thead className="bg-blue-700 px-4 text-white uppercase tracking-wider  sticky top-0 z-10">
                  <tr className="px-4">
                    <th className="p-1">User ID</th>
                    <th className="p-1">Service Name</th>
                    <th className="p-1">Reference ID</th>
                    <th className="p-1">Total Records</th>
                    <th className="p-1">Processed Records</th>
                    <th className="p-1">Status</th>
                    <th className="p-1">Created At</th>
                    <th className="p-1">Updated At</th>
                    <th className="p-1">Retry</th>
                    <th className="p-1">Force Complete</th>
                    <th className="p-1">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {reportData.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-gray-50 ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                      }`}
                    >
                      <td className="p-1">{item.user_id}</td>
                      <td className="">
                        {item.service?.service_name || "N/A"}
                      </td>

                      <td
                        className="p-1 truncate max-w-[220px]"
                        title={item.reference_id}
                      >
                        {item.reference_id}
                      </td>

                      <td className="p-1">{item.total_records}</td>
                      <td className="p-1">{item.processed_records}</td>
                      <td className="p-1">
                        <span
                          className={`px-2 py-1 rounded-full text-[10px] font-semibold ${
                            item.status === "completed"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {item.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-1 whitespace-nowrap">
                        {new Date(item.created_at).toLocaleString()}
                      </td>
                      <td className="p-1 whitespace-nowrap">
                        {new Date(item.updated_at).toLocaleString()}
                      </td>
                      {/* Dispatch Button - Primary Ghost */}
                      <td className="p-1">
                        <button
                          onClick={() => {
                            setRetryClicked(true);
                            setDataForActions(item);
                          }}
                          disabled={item.status !== "pending"}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors bg-indigo-600 text-indigo-50 hover:bg-indigo-800 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Dispatch"
                        >
                          Retry
                        </button>
                      </td>

                      {/* Force Complete Button - Soft Red */}
                      <td className="p-1">
                        <button
                          onClick={() => {
                            setForceCompleteClicked(true);
                            setDataForActions(item);
                          }}
                          disabled={item.status !== "processing"}
                          className="px-3 py-1.5 text-[10px] font-semibold rounded-lg transition-colors bg-red-600 text-red-50 hover:bg-red-800 hover:text-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Force Complete"
                        >
                          Force Complete
                        </button>
                      </td>

                      <td className="p-1 ">
                        <button
                          //   onClick={() => handleDownload(item.reference_id)}
                          disabled={item.status !== "completed"}
                          className="p-1 disabled:opacity-40"
                          title={
                            item.status !== "completed"
                              ? "Only completed reports can be downloaded"
                              : "Download"
                          }
                        >
                          <Download className="w-6 h-6 text-green-600" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination && (
          <div className="flex items-center justify-between mt-4 px-2 text-sm text-gray-600 w-full max-w-4xl">
            <span>
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            <div className="flex gap-2">
              <button
                disabled={!pagination.prev_page_url}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded border disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Prev
              </button>
              <button
                disabled={!pagination.next_page_url}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded border disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {reportData?.length === 0 && (
          <div className="text-gray-500 text-center py-8">
            No reports found. Try adjusting your filters.
          </div>
        )}

        {forceCompleteClicked && (
          <ConfirmModal
            onConfirm={handleForceComplete}
            isOpen={forceCompleteClicked}
            onCancel={() => setForceCompleteClicked(false)}
            loading={isLoading}
            confirmText="Execute Force Complete"
          />
        )}

        {retryClicked && (
          <ConfirmModal
            onConfirm={handleRetry}
            isOpen={retryClicked}
            type={"green"}
            title="Retry Cron Job Processing"
            onCancel={() => setRetryClicked(false)}
            loading={isLoading}
            confirmText="Yes, Sure! "
          />
        )}
      </div>
    </>
  );
}

export default BulkReport;
